import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { triageKeys } from "@/hooks/use-triage";

/**
 * Wire-level type stored on existing source rows. `meeting`, `survey`, `email`,
 * `slack`, and `transcript` are LEGACY values — kept for read compatibility with
 * pre-collapse data. New inserts must use `NewSourceType` (paste | document).
 * The Postgres enum stays permissive; narrowing is enforced here at compile time.
 */
export type SourceType =
  | "meeting"
  | "survey"
  | "document"
  | "email"
  | "slack"
  | "transcript"
  | "paste";

/** Type for newly created sources. Paste-style inputs → `paste`, files → `document`. */
export type NewSourceType = "paste" | "document";

export interface SourceCreateBody {
  type: NewSourceType;
  name: string;
  sourceDate?: string;
  durationMin?: number | null;
  rawText?: string | null;
  s3Key?: string | null;
}

export interface SourceLatestRun {
  status: "running" | "succeeded" | "failed";
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

export interface Source {
  id: string;
  solutionId: string;
  externalKey: string | null;
  type: SourceType;
  name: string;
  sourceDate: string;
  durationMin: number | null;
  s3Key: string | null;
  rawText: string | null;
  createdAt: string;
  participantStakeholderIds: string[];
  latestRun: SourceLatestRun | null;
  itemsExtracted: number | null;
  processedAt: string | null;
}

export interface TranscriptLine {
  id: string;
  sourceId: string;
  stakeholderId: string | null;
  tsLabel: string | null;
  text: string;
  highlighted: string | null;
  sortOrder: number;
}

export interface SourceDetail extends Source {
  transcriptLines: TranscriptLine[];
  processedAt: string | null;
  lastRunId: string | null;
}

export interface ReprocessPreview {
  decidedCount: number;
  viewedCount: number;
  untouchedCount: number;
}

export interface SourceTriageItem {
  id: string;
  type: string;
  extracted: string;
  confidence: "low" | "medium" | "high";
  suggestedStatus: "pending" | "in-scope" | "in-review" | "out-of-scope" | "deferred";
  effectiveStatus: "pending" | "in-scope" | "in-review" | "out-of-scope" | "deferred";
  createdAt: string;
}

export type RunStatus = "running" | "succeeded" | "failed";

export interface ProcessingRun {
  runId: string;
  status: RunStatus;
  itemsCreated: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface ProcessResult {
  runId: string;
  itemsCreated: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
}

export const sourceKeys = {
  all: ["sources"] as const,
  list: (solutionId: string) => ["sources", "list", solutionId] as const,
  detail: (sourceId: string) => ["sources", "detail", sourceId] as const,
  preview: (sourceId: string) => ["sources", "preview", sourceId] as const,
  triageItems: (sourceId: string) => ["sources", "triage-items", sourceId] as const,
} as const;

export const runKeys = {
  detail: (runId: string) => ["runs", "detail", runId] as const,
} as const;

export function useSources(solutionId: string) {
  return useQuery({
    queryKey: sourceKeys.list(solutionId),
    queryFn: async (): Promise<Source[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/sources`);
      if (!res.ok) throw new Error("Failed to fetch sources");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
    // Auto-refresh while any source is in a 'running' state so the list
    // reflects newly-finished extractions without manual reload.
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasRunning = data?.some((s) => s.latestRun?.status === "running");
      return hasRunning ? 5000 : false;
    },
  });
}

export function useSource(sourceId: string | null) {
  return useQuery({
    queryKey: sourceKeys.detail(sourceId ?? ""),
    queryFn: async (): Promise<SourceDetail> => {
      const res = await fetch(`/api/sources/${sourceId}`);
      if (!res.ok) throw new Error("Failed to fetch source");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(sourceId),
  });
}

export function useReprocessPreview(sourceId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: sourceKeys.preview(sourceId ?? ""),
    queryFn: async (): Promise<ReprocessPreview> => {
      const res = await fetch(`/api/sources/${sourceId}/reprocess-preview`);
      if (!res.ok) throw new Error("Failed to fetch reprocess preview");
      const json = await res.json();
      return json.data;
    },
    enabled: enabled && Boolean(sourceId),
    staleTime: 0,
  });
}

export function useSourceTriageItems(sourceId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: sourceKeys.triageItems(sourceId ?? ""),
    queryFn: async (): Promise<SourceTriageItem[]> => {
      const res = await fetch(`/api/sources/${sourceId}/triage-items`);
      if (!res.ok) throw new Error("Failed to fetch source triage items");
      const json = await res.json();
      return json.data;
    },
    enabled: enabled && Boolean(sourceId),
  });
}

export function useCreateSource(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SourceCreateBody): Promise<Source> => {
      const res = await fetch(`/api/solutions/${solutionId}/sources`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Failed to add source");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: sourceKeys.list(solutionId) }),
    onError: () => toast.error("Failed to add source"),
  });
}

/* ── Process AI ────────────────────────────────────────────────── */

/** Typed error codes the mutation can throw. The component checks `code`. */
export type ProcessErrorCode =
  | "REPROCESS_CONFIRMATION_REQUIRED"
  | "RUN_IN_PROGRESS"
  | "SOURCE_TOO_LARGE"
  | "SOURCE_EMPTY"
  | "TOKEN_BUDGET_EXCEEDED"
  | "AI_NOT_CONFIGURED"
  | "EXTRACTION_FAILED"
  | "CANCELLED"
  | "UNKNOWN";

export class ProcessError extends Error {
  constructor(
    public readonly code: ProcessErrorCode,
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ProcessError";
  }
}

/** Sentinel server-side errorMessage written by the cancel endpoint. */
const CANCEL_ERROR_MESSAGE = "cancelled by user";

/**
 * Parse a Response body as JSON, converting a malformed/empty body into a
 * friendly UNKNOWN ProcessError instead of leaking the browser's raw
 * "Unexpected end of JSON input" SyntaxError into a toast.
 */
async function parseJsonOrThrow(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    throw new ProcessError(
      "UNKNOWN",
      "The server didn't respond properly. Please try again — if it keeps happening, contact support.",
      res.status,
    );
  }
}

async function fetchRun(runId: string): Promise<ProcessingRun> {
  const res = await fetch(`/api/source-processing-runs/${runId}`);
  if (!res.ok) {
    throw new ProcessError(
      "UNKNOWN",
      "Lost track of the processing run. Please refresh and try again.",
      res.status,
    );
  }
  const json = (await parseJsonOrThrow(res)) as { data: ProcessingRun };
  return json.data;
}

async function pollUntilTerminal(runId: string): Promise<ProcessingRun> {
  const start = Date.now();
  const timeoutMs = 5 * 60 * 1000;
  let attempt = 0;
  const delays = [3000, 3000, 5000, 5000, 10000];

  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, delays[Math.min(attempt, delays.length - 1)]));
    attempt++;
    const run = await fetchRun(runId);
    if (run.status !== "running") return run;
  }
  throw new ProcessError("EXTRACTION_FAILED", "Processing timed out", 504, { runId });
}

export interface ProcessVariables {
  sourceId: string;
  reprocess: boolean;
  onRunStarted?: (runId: string) => void;
}

/**
 * Run the Process-AI mutation for a source. Single instance per solution-page;
 * sourceId is per-call. POSTs once to start the run (always returns 202 + runId),
 * then polls until terminal. The list query is invalidated immediately after
 * the POST returns so the source row reflects "Processing…" within ~200ms;
 * a second invalidation fires on terminal status to refresh items/processedAt.
 */
export function useProcessSource(solutionId: string) {
  const qc = useQueryClient();
  return useMutation<ProcessResult, ProcessError, ProcessVariables>({
    mutationFn: async ({ sourceId, reprocess, onRunStarted }) => {
      const url = `/api/sources/${sourceId}/process${reprocess ? "?reprocess=true" : ""}`;
      const res = await fetch(url, { method: "POST" });
      const body = (await parseJsonOrThrow(res)) as
        | { runId: string; status: "running" }
        | { error: { code: string; message: string; details?: Record<string, unknown> } };

      // Error path — server returned a typed error envelope.
      if ("error" in body) {
        const { code, message, details } = body.error;
        const typedCode = isProcessErrorCode(code) ? code : "UNKNOWN";

        // 423 RUN_IN_PROGRESS: piggyback onto the existing run instead of erroring out.
        if (typedCode === "RUN_IN_PROGRESS") {
          const existingRunId = typeof details?.runId === "string" ? details.runId : null;
          if (existingRunId) {
            onRunStarted?.(existingRunId);
            qc.invalidateQueries({ queryKey: sourceKeys.list(solutionId) });
            return await awaitRun(existingRunId);
          }
        }
        throw new ProcessError(typedCode, message, res.status, details);
      }

      // Happy path — got runId, poll until terminal.
      if (!("runId" in body) || body.status !== "running") {
        throw new ProcessError("UNKNOWN", "Unexpected response shape from server", res.status);
      }
      onRunStarted?.(body.runId);
      // Refresh the list right away so the row flips to "Processing…" before
      // the next polling tick fires.
      qc.invalidateQueries({ queryKey: sourceKeys.list(solutionId) });
      return await awaitRun(body.runId);
    },
    onSuccess: (result, variables) => {
      toast.success(`Extracted ${result.itemsCreated} items from this source`);
      qc.invalidateQueries({ queryKey: triageKeys.all });
      qc.invalidateQueries({ queryKey: sourceKeys.detail(variables.sourceId) });
      qc.invalidateQueries({ queryKey: sourceKeys.preview(variables.sourceId) });
      qc.invalidateQueries({ queryKey: sourceKeys.triageItems(variables.sourceId) });
      qc.invalidateQueries({ queryKey: sourceKeys.list(solutionId) });
    },
    onError: (err, variables) => {
      // REPROCESS_CONFIRMATION_REQUIRED is expected control flow — the caller
      // opens the confirm modal in response.
      // CANCELLED is also expected — the cancel mutation owns its toast.
      // Refresh list on terminal errors so a "Processing…" row settles back to
      // its actual state (failed or not-yet-processed).
      qc.invalidateQueries({ queryKey: sourceKeys.list(solutionId) });
      qc.invalidateQueries({ queryKey: sourceKeys.detail(variables.sourceId) });
      if (err.code === "REPROCESS_CONFIRMATION_REQUIRED") return;
      if (err.code === "CANCELLED") return;
      const friendly = friendlyMessage(err);
      toast.error(friendly);
    },
  });
}

/** Poll a run to terminal status. Discriminates user-cancellation from other failures. */
async function awaitRun(runId: string): Promise<ProcessResult> {
  const run = await pollUntilTerminal(runId);
  if (run.status === "failed") {
    if (run.errorMessage === CANCEL_ERROR_MESSAGE) {
      throw new ProcessError("CANCELLED", "Cancelled by user", 200, { runId });
    }
    throw new ProcessError("EXTRACTION_FAILED", run.errorMessage ?? "Extraction failed", 502, {
      runId,
    });
  }
  return {
    runId: run.runId,
    itemsCreated: run.itemsCreated,
    inputTokens: run.inputTokens,
    outputTokens: run.outputTokens,
    cachedInputTokens: run.cachedInputTokens,
  };
}

/** Cancel a running processing run. Idempotent server-side. */
export function useCancelRun() {
  const qc = useQueryClient();
  return useMutation<{ runId: string; status: "cancelled" | "already-terminal" }, Error, string>({
    mutationFn: async (runId) => {
      const res = await fetch(`/api/source-processing-runs/${runId}/cancel`, { method: "POST" });
      if (!res.ok) {
        throw new Error(`Failed to cancel (${res.status})`);
      }
      const json = (await parseJsonOrThrow(res)) as {
        data: { runId: string; status: "cancelled" | "already-terminal" };
      };
      return json.data;
    },
    onSuccess: (result) => {
      // "already-terminal" means the run finished before cancel got there —
      // stay silent; the processSource mutation will toast its outcome.
      if (result.status === "cancelled") {
        toast("Cancelled");
      }
      qc.invalidateQueries({ queryKey: runKeys.detail(result.runId) });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
}

function isProcessErrorCode(code: string): code is ProcessErrorCode {
  return [
    "REPROCESS_CONFIRMATION_REQUIRED",
    "RUN_IN_PROGRESS",
    "SOURCE_TOO_LARGE",
    "SOURCE_EMPTY",
    "TOKEN_BUDGET_EXCEEDED",
    "AI_NOT_CONFIGURED",
    "EXTRACTION_FAILED",
    "CANCELLED",
  ].includes(code);
}

function friendlyMessage(err: ProcessError): string {
  switch (err.code) {
    case "SOURCE_TOO_LARGE":
      return "This source is too large to process (max 250KB of text).";
    case "SOURCE_EMPTY":
      return "This source has no text to process.";
    case "TOKEN_BUDGET_EXCEEDED":
      return "This solution has reached its monthly AI processing budget.";
    case "AI_NOT_CONFIGURED":
      return "AI processing is not configured for this deployment — contact your admin.";
    case "EXTRACTION_FAILED":
      return `AI processing failed: ${err.message}`;
    case "UNKNOWN":
      return (
        err.message ||
        "Something went wrong on the server. Please try again — if it keeps happening, contact support."
      );
    default:
      return err.message;
  }
}
